import React from 'react';
import {
  Box,
  Button,
  Typography,
  Grid,
  Chip,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';

import {
  CheckCircle as ApproveIcon,
  PlayArrow as StartIcon,
  Stop as EndIcon,
  Cancel as AbortIcon,
} from '@mui/icons-material';
import BorderedSection from '../../shared/borderedSection';
import { PlmEvent } from './types';
import {
  EnumPlmEventResponseResponseStatus,
  useApprovePlmEventResponseMutation,
  usePlmEventResponseEventsByIdsSubscription,
  useUpdatePlmEventResponseMutation,
} from '../../../../types/generated-types';
import { updateCacheFromSubscriptionEvent } from '../../../../helpers/subscriptionUtils';
import { Notifier } from '../../../system/services/notificationManager';
import { humanize } from '../../../ui/shell/utilities';
import '../ui/plm.css';

interface PlmEventResponseDetailViewerProps {
  plmEvent?: PlmEvent;
}

export const PlmEventResponseDetailViewer = ({
  plmEvent,
}: PlmEventResponseDetailViewerProps) => {
  const [approvePlmEventResponse] = useApprovePlmEventResponseMutation();
  const [updatePlmEventResponse] = useUpdatePlmEventResponseMutation();

  usePlmEventResponseEventsByIdsSubscription({
    variables: { _ids: [(plmEvent?.responses ?? [])[0]?._id ?? ''] },
    fetchPolicy: 'no-cache',
    onData: updateCacheFromSubscriptionEvent,
  });

  if (!plmEvent) {
    return <Typography>No PLM event data available</Typography>;
  }

  // Get the single response for this event
  const response = plmEvent.responses?.[0];

  const formatDateTime = (timestamp: number | null | undefined) => {
    if (!timestamp) return '... pending ...';
    return new Date(timestamp).toLocaleString();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return '#4caf50';
      case 'ACTIVE':
        return '#2196f3';
      case 'DECLINED':
        return '#f44336';
      case 'PENDING_APPROVAL':
        return '#ff9800';
      case 'SCHEDULED':
        return '#9c27b0';
      case 'SUSPENDED':
        return '#ff5722';
      case 'TERMINATED':
        return '#795548';
      case 'RESUMED':
        return '#00bcd4';
      default:
        return '#757575';
    }
  };

  const handleApprove = async () => {
    if (!response?._id) {
      Notifier.error('No response found to approve');
      return;
    }

    try {
      await approvePlmEventResponse({
        variables: { _id: response._id },
      });
      Notifier.success('Response approved successfully');
    } catch (error) {
      console.error('Error approving response:', error);
      Notifier.error('Failed to approve response');
    }
  };

  const handleStart = async () => {
    if (!response?._id) {
      Notifier.error('No response found to start');
      return;
    }

    try {
      await updatePlmEventResponse({
        variables: {
          _id: response._id,
          plmEventResponse: {
            responseStatus: EnumPlmEventResponseResponseStatus.Active,
          },
        },
      });
      Notifier.success('Response started successfully');
    } catch (error) {
      console.error('Error starting response:', error);
      Notifier.error('Failed to start response');
    }
  };

  const handleEnd = async () => {
    if (!response?._id) {
      Notifier.error('No response found to end');
      return;
    }

    try {
      await updatePlmEventResponse({
        variables: {
          _id: response._id,
          plmEventResponse: {
            responseStatus: EnumPlmEventResponseResponseStatus.Completed,
          },
        },
      });
      Notifier.success('Response ended successfully');
    } catch (error) {
      console.error('Error ending response:', error);
      Notifier.error('Failed to end response');
    }
  };

  const handleCancel = () => {
    // TODO: Implement cancel functionality
    console.log('Cancel clicked');
  };

  const handleOptOut = () => {
    // TODO: Implement out out functionality
    console.log('Opt Out clicked');
  };

  return response ? (
    <Box>
      <Typography variant="h4" gutterBottom>
        Response Details
      </Typography>

      {/* Response Operations Section */}
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <BorderedSection
            title="Manage Response"
            ccStyle={{
              marginBottom: '6px',
            }}
          >
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              {response.responseStatus ===
              EnumPlmEventResponseResponseStatus.PendingApproval ? (
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<ApproveIcon />}
                  onClick={handleApprove}
                  disabled={!response}
                >
                  Approve
                </Button>
              ) : null}
              {response.responseStatus ===
              EnumPlmEventResponseResponseStatus.Active ? (
                <Button
                  variant="contained"
                  color="info"
                  startIcon={<EndIcon />}
                  onClick={handleEnd}
                  disabled={!response}
                >
                  End
                </Button>
              ) : null}
              {response.responseStatus ===
              EnumPlmEventResponseResponseStatus.Scheduled ? (
                <Button
                  variant="contained"
                  color="error"
                  startIcon={<AbortIcon />}
                  onClick={handleCancel}
                  disabled={!response}
                >
                  Cancel
                </Button>
              ) : null}
              {response.responseStatus ===
              EnumPlmEventResponseResponseStatus.Scheduled ? (
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<StartIcon />}
                  onClick={handleStart}
                  disabled={!response}
                >
                  Start
                </Button>
              ) : null}
              {response.responseStatus ===
              EnumPlmEventResponseResponseStatus.PendingApproval ? (
                <Button
                  variant="contained"
                  color="error"
                  startIcon={<AbortIcon />}
                  onClick={handleOptOut}
                  disabled={!response}
                >
                  Opt Out
                </Button>
              ) : null}
            </Box>
          </BorderedSection>
        </Grid>

        {response ? (
          <Grid item xs={12}>
            <Box sx={{ mt: 4 }}>
              <BorderedSection
                collapsible={true}
                initiallyExpanded={true}
                collapsedWidget={
                  <Box sx={{ py: 1 }}>
                    <Typography variant="body1">
                      <strong>Curtailment Plan:</strong>{' '}
                      {response.curtailmentPlan?.name || 'Unknown Plan'}
                    </Typography>
                  </Box>
                }
                title="Response Details"
              >
                <>
                  <Grid container spacing={3}>
                    {/* Basic Information */}
                    <Grid item xs={12} md={6}>
                      <BorderedSection title="Basic Information">
                        <Grid
                          container
                          spacing={2}
                          className="plm-inner-grid-container"
                        >
                          <Grid item className="item-wrapper" xs={12}>
                            <List className="plm-ul" dense={false}>
                              <ListItem>
                                <ListItemText
                                  className="plm-listItem-text"
                                  primary={
                                    <Typography
                                      className="label"
                                      component="div"
                                    >
                                      Program:
                                    </Typography>
                                  }
                                  secondary={
                                    <Typography
                                      className="sub-label"
                                      component="div"
                                    >
                                      {response.event?.program?.name || 'N/A'}
                                    </Typography>
                                  }
                                />
                              </ListItem>
                              <ListItem sx={{ justifyContent: 'space-evenly' }}>
                                <ListItemText
                                  className="plm-listItem-text"
                                  sx={{ flex: 1 }}
                                  primary={
                                    <Typography
                                      className="label"
                                      component="div"
                                    >
                                      Curtailment Plan:
                                    </Typography>
                                  }
                                  secondary={
                                    <Typography
                                      className="sub-label"
                                      component="div"
                                    >
                                      {response.curtailmentPlan?.name || 'N/A'}
                                    </Typography>
                                  }
                                />
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 2,
                                    flex: 1,
                                  }}
                                >
                                  {(response.responseStatus ===
                                    EnumPlmEventResponseResponseStatus.PendingApproval ||
                                    response.responseStatus ===
                                      EnumPlmEventResponseResponseStatus.Scheduled) && (
                                    <Button
                                      variant="contained"
                                      size="small"
                                      onClick={() => {
                                        // TODO: Implement change plan functionality
                                        console.log('Change Plan clicked');
                                      }}
                                    >
                                      Change Plan
                                    </Button>
                                  )}
                                </Box>
                              </ListItem>
                              <ListItem>
                                <ListItemText className="plm-listItem-text">
                                  <Typography
                                    className="label"
                                    component="span"
                                  >
                                    Response Status:
                                  </Typography>
                                  <Chip
                                    label={humanize(
                                      `${response.responseStatus}`,
                                    )}
                                    sx={{
                                      backgroundColor: getStatusColor(
                                        response.responseStatus,
                                      ),
                                      color: 'white',
                                      borderRadius: '4px',
                                      fontWeight: 'bold',
                                      minWidth: '125px',
                                      marginLeft: '10px',
                                    }}
                                  />
                                </ListItemText>
                              </ListItem>
                            </List>
                          </Grid>
                        </Grid>
                      </BorderedSection>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <BorderedSection title="Timestamps">
                        <Grid
                          container
                          spacing={2}
                          className="plm-inner-grid-container"
                        >
                          {/* Scheduled Start and End on same line */}
                          <Grid className="item-wrapper" item xs={12}>
                            <List className="plm-ul" dense={false}>
                              <ListItem>
                                <ListItemText
                                  className="plm-listItem-text"
                                  primary={
                                    <Typography
                                      className="label"
                                      component="div"
                                    >
                                      Scheduled Start:
                                    </Typography>
                                  }
                                  secondary={
                                    <Typography
                                      className="sub-label"
                                      component="div"
                                    >
                                      {formatDateTime(response.scheduledStart)}
                                    </Typography>
                                  }
                                />
                              </ListItem>
                              <ListItem>
                                <ListItemText
                                  className="plm-listItem-text"
                                  primary={
                                    <Typography
                                      className="label"
                                      component="div"
                                    >
                                      Scheduled End:
                                    </Typography>
                                  }
                                  secondary={
                                    <Typography
                                      className="sub-label"
                                      component="div"
                                    >
                                      {formatDateTime(response.scheduledEnd)}
                                    </Typography>
                                  }
                                />
                              </ListItem>
                              <ListItem>
                                <ListItemText
                                  className="plm-listItem-text"
                                  primary={
                                    <Typography
                                      className="label"
                                      component="div"
                                    >
                                      Actual Start:
                                    </Typography>
                                  }
                                  secondary={
                                    <Typography
                                      className="sub-label"
                                      component="div"
                                    >
                                      {formatDateTime(response.actualStart)}
                                    </Typography>
                                  }
                                />
                              </ListItem>
                              <ListItem>
                                <ListItemText
                                  className="plm-listItem-text"
                                  primary={
                                    <Typography
                                      className="label"
                                      component="div"
                                    >
                                      Actual End:
                                    </Typography>
                                  }
                                  secondary={
                                    <Typography
                                      className="sub-label"
                                      component="div"
                                    >
                                      {formatDateTime(response.actualEnd)}
                                    </Typography>
                                  }
                                />
                              </ListItem>
                              <ListItem>
                                <ListItemText
                                  className="plm-listItem-text"
                                  primary={
                                    <Typography
                                      className="label"
                                      component="div"
                                    >
                                      Response Sent At:
                                    </Typography>
                                  }
                                  secondary={
                                    <Typography
                                      className="sub-label"
                                      component="div"
                                    >
                                      {formatDateTime(response.responseSentAt)}
                                    </Typography>
                                  }
                                />
                              </ListItem>
                            </List>
                          </Grid>
                        </Grid>
                      </BorderedSection>
                    </Grid>
                  </Grid>
                </>
              </BorderedSection>
            </Box>
          </Grid>
        ) : (
          <Box sx={{ mt: 3 }}>
            <BorderedSection title="Response Status">
              <Typography variant="body1" color="textSecondary">
                No response has been created for this PLM event yet.
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                A response will be created when the event is processed by the
                system.
              </Typography>
            </BorderedSection>
          </Box>
        )}
      </Grid>
    </Box>
  ) : null;
};
